<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import UploadField from "@/components/UploadField.vue";
import RichTextField from "@/components/RichTextField.vue";
import { uploadImage, uploadVideo, uploadFile } from "@/lib/uploads";
import { addTemplate } from "./api";

const DEFAULT_IMG = "https://firebasestorage.googleapis.com/v0/b/aspial.appspot.com/o/announcements%2Faspial.jpg?alt=media&token=ea877aa0-2ec4-4caa-9342-3280ceafb2fc";
const BTN_LINK_ITEMS = ["Kronos", "IHP", "Success Factor", "Concur", "Zendesk", "Campaign", "Videos", "More Options"];
const router = useRouter();
const saving = ref(false), success = ref(false), error = ref(""), validationError = ref("");

const form = ref({ notif_title: "", notif_content: "", title: "", content: "", btnTitle: "", btnLink: "", redirectUrl: "", imgUrl: DEFAULT_IMG, isExternalBrowser: false, isDynamicUrl: false });

const imageUploader = (file, p) => uploadImage("messages_template", file, p);
const redirectUploader = (file, p) => {
  if (file.type.includes("image")) return uploadImage("messages_template", file, p);
  if (file.type.includes("video")) return uploadVideo(file, p, "messages_template");
  return uploadFile("messages_template", file, p);
};

async function submit() {
  validationError.value = "";
  if (!form.value.title.trim()) { validationError.value = "Inbox Title is required."; return; }
  saving.value = true; error.value = ""; success.value = false;
  try {
    await addTemplate({ ...form.value });
    success.value = true;
    form.value = { notif_title: "", notif_content: "", title: "", content: "", btnTitle: "", btnLink: "", redirectUrl: "", imgUrl: DEFAULT_IMG, isExternalBrowser: false, isDynamicUrl: false };
  } catch { error.value = "Error occurred. Please try again."; }
  finally { saving.value = false; }
}
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'messages' })">Back</VBtn>
    <h1 class="text-h5 mb-4">Create Template</h1>
    <VCard class="pa-4" max-width="760">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="validationError" type="warning" class="mb-4">{{ validationError }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">Template created successfully.</VAlert>
      <form @submit.prevent="submit">
        <UploadField v-model="form.imgUrl" kind="image" label="Cover Photo" :uploader="imageUploader" class="mb-4" />
        <VRow>
          <VCol cols="12" md="6"><VTextField v-model="form.notif_title" label="Notification Title" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.notif_content" label="Notification Content" /></VCol>
          <VCol cols="12"><VTextField v-model="form.title" label="Inbox Title" /></VCol>
          <VCol cols="12"><RichTextField v-model="form.content" label="Inbox Content" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.btnTitle" label="Button Title" /></VCol>
          <VCol cols="12" md="6">
            <VAutocomplete v-model="form.btnLink" label="Button Link" :items="BTN_LINK_ITEMS" clearable placeholder="—" auto-select-first />
          </VCol>
          <VCol v-if="!form.btnLink" cols="12"><VTextField v-model="form.redirectUrl" label="Button URL" /></VCol>
          <VCol v-if="!form.btnLink" cols="12">
            <UploadField v-model="form.redirectUrl" kind="file" label="Upload PDF / Image for button" accept="image/*,application/pdf,video/*" :uploader="redirectUploader" />
          </VCol>
          <VCol cols="6"><VSwitch v-model="form.isExternalBrowser" label="External Browser" hide-details /></VCol>
          <VCol cols="6"><VSwitch v-model="form.isDynamicUrl" label="Dynamic User ID" hide-details /></VCol>
        </VRow>
        <div class="d-flex gap-2 mt-4">
          <VBtn type="submit" :loading="saving">Save Template</VBtn>
          <VBtn variant="tonal" @click="router.push({ name: 'messages' })">Cancel</VBtn>
        </div>
      </form>
    </VCard>
  </section>
</template>
